<template lang="html">
  <div class="col">

    <div v-if="hasPhoneMFAFactor">
      <h3>Security</h3>
      <p><strong>Your account is secure.</strong> Nice work! You're enrolled in phone multi-factor authentication (MFA).</p>
    </div>
    <div v-if="!hasPhoneMFAFactor">
      <h3 class="light-weight">Enable Multi-Factor Authentication (MFA)</h3>
      <p>Enable MFA to keep your account secure and begin using features that require HIPAA compliance.</p>
      <div v-if="mfaStep=='enable'">
        <p><em>You'll need a phone number that can receive text messages to get started.</em></p>
        <button v-if="!mfaLoading" @click="enableMFA" class="btn btn-app">Enable Phone MFA<i class="far fa-angle-right fa-fw fa-right"></i></button>
        <i v-if="mfaLoading" class="far fa-spinner-third fa-spin fa-2x"></i>
        <list-errors :errors="errors"></list-errors>
        <hr />
      </div>
      <div v-show="mfaStep == 'recaptcha'">
        <p><em>Please solve the Recaptcha below to continue:</em></p>
        <div id="recaptchaContainer"></div>
        <list-errors :errors="errors"></list-errors>
        <hr />
      </div>
      <div v-if="mfaStep=='getPhoneNumber'">
        <p><em>You will receive an SMS text message after adding your phone number.</em></p>
        <!-- <input v-model="mfaPhone" name="mfaPhone" class="form-control" placeholder="Enter phone number in format xxx-xxx-xxxx" type="tel" pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}" @keyup.enter="saveMFAPhone"/> -->
         <the-mask mask="###-###-####" class="form-control" v-model="mfaPhone" placeholder="Enter phone number in format xxx-xxx-xxxx" @keyup.native.enter="saveMFAPhone" />
        <button v-if="!mfaLoading" @click="saveMFAPhone" class="btn btn-app"><i class="far fa-plus fa-fw fa-left"></i>Add Phone</button>
        <i v-if="mfaLoading" class="far fa-spinner-third fa-spin fa-2x"></i>
        <list-errors :errors="errors"></list-errors>
        <hr v-if="!errors.length" />
      </div>
      <div v-if="mfaStep == 'phoneCode'">
        <input v-model="mfaCode" name="mfaCode" class="form-control" placeholder="Enter code from your text message" type="text" @keyup.enter="enrollWithPhoneCode"/>

        <list-errors :errors="errors"></list-errors>

        <p><button v-if="!loading" @click.prevent="enrollWithPhoneCode" class="btn btn-app">Continue with Code<i class="far fa-angle-right fa-fw"></i></button></p>

        <p v-if="loading"><i class="far fa-fw fa-pulse fa-spinner"></i></p>
        <p v-if="mfaSuccess"><i class="far fa-check success-color "></i> Successfully enrolled in MFA</p>
        <hr />
      </div>
    </div>
    
    <div v-if="!reauthenticate">

      <h3 class="light-weight">Change Your Email Address</h3>
      <input v-model="newEmail" name="email" class="form-control" placeholder="Enter email" type="text" />

      <list-errors v-if="!mfaStep || mfaStep!=='getPhoneNumber'" :errors="errors"></list-errors>

      <p v-if="updated"><i class="far fa-check"></i> account successfully updated</p>

      <button v-if="!loading" @click.prevent="updateUser" class="btn btn-app">Save All Changes<i class="far fa-angle-right fa-fw fa-right"></i></button>
      <i v-if="loading" class="far fa-spinner-third fa-spin fa-2x"></i>
    </div>
    <div v-if="reauthenticate">
      <h3>Please Log In Again</h3>
      <list-errors v-if="!mfaStep || mfaStep!=='getPhoneNumber'" :errors="errors"></list-errors>
      <button @click.prevent="logOut" class="btn btn-app">Log In<i class="far fa-angle-right fa-fw fa-right"></i></button>
    </div>

    <hr />

    <h3 class="light-weight">Change Your Password</h3>
    <router-link to="/reset-password" class="btn btn-app">Reset Password<i class="far fa-angle-right fa-fw fa-right"></i></router-link>
  </div>
</template>

<script>
import Vue from 'vue'
import {mapGetters} from 'vuex'
import Firebase from 'firebase/compat/app'
import 'firebase/auth'
import ListErrors from '../components/ListErrors.vue'
import {TheMask} from 'vue-the-mask'

export default {
  props: ['plans'],
  components: {
    ListErrors, TheMask
  },
  data () {
    return {
      newEmail: null,
      errors: [],
      loading: false,
      updated: false,
      reauthenticate: false,
      // mfa
      mfaLoading: false,
      mfaStep: 'enable',
      mfaPhone: null,
      mfaResolver: null,
      mfaCode: null,
      mfaVerificationId: null,
      recaptchaVerifier: null,
      mfaSession: null,
      mfaSuccess: false,
    }
  },
  computed: {
    ...mapGetters(['user', 'user_props', 'account']),
    email() {
      return this.user.email
    },
    hasPhoneMFAFactor() {
      if(this.user && this.user.multiFactor && this.user.multiFactor.enrolledFactors && this.user.multiFactor.enrolledFactors.some(f => f.factorId == "phone")) {
        return true;
      }
      return false;
    },
  },
  methods: {
    logOut () {
      this.$firebase.logOut()
    },
    updateUser() {
      let promises = [];
      let user = Firebase.auth().currentUser;
      this.loading = true;
      this.updated = false;
      let disallowedNames = ["", " ", "  "];
      // reset errors
      this.errors = [];

      let data = {};

      // if email changed, update it
      if(this.newEmail !== this.user.email) {
        let updateEmailPromise = user.updateEmail(this.newEmail);
        data[`/user_props/${user.uid}/email`] = this.newEmail;
        promises.push(updateEmailPromise)
      }

      if(promises.length) {
        Promise.all(promises)
          .then(results => {
            // const [emailResult, profileResult] = results;
            // console.log("updateUser results:",results);
            this.loading = false;
            this.updated = true;
            // if email update w/ firebase auth as successful, then store it in user_props
            if(Object.keys(data).length) {
              return Vue.db.ref().update(data);
            }
            return true;
          })
          .catch(error => {
            // console.log("updateUser error:",error);
            this.loading = false;

            if(error.code == "auth/invalid-email") {
              this.errors.push("Oops, that's not a valid email address.")
            }
            else if(error.code =="auth/requires-recent-login") {
              this.errors.push("To protect your security, you need to log in again before you can update your email address.")
              this.reauthenticate = true;
            }
            else if(error.code =="auth/network-request-failed") {
              this.errors.push("We couldn't udpate your account because of a network error. Please check your internet connection and try again.")
            }
            else if(error.message) {
              this.errors.push(error.message);
            }
          });
      } else {
        this.errors.push("No changes to save. Edit your email address or display name first.")
        this.loading = false;
      }

    },
    // MFA
    enableMFA() {
      this.mfaLoading = true;
      this.user.multiFactor.getSession().then((multiFactorSession) => {
        this.mfaSession = multiFactorSession;
        this.createRecaptcha();
        // this.mfaStep = 'getPhoneNumber';
        this.mfaStep = 'recaptcha';
        this.mfaLoading = false;
      })
      .catch(err => console.log(err));
    },
    async saveMFAPhone() {
      this.mfaLoading = true;
      if(this.mfaPhone && this.mfaPhone.length==10) {
        const phoneFormatted = this.mfaPhone.replaceAll('-','');
        // console.log({phoneFormatted});
        var phoneInfoOptions = {
          phoneNumber: `+1${phoneFormatted}`,
          session: this.mfaSession
        };
        let result = await this.$firebase.sendPhoneVerification(phoneInfoOptions, this.recaptchaVerifier);
        // console.log('sendPhoneVerification result', result);
        if(result && result.code && result.code == "auth/requires-recent-login") {
          this.errors.push("To protect your security, you need to log in again before you can update your email address.")
          this.reauthenticate = true;
          this.mfaLoading = false;
        }
        else if(result && result.code && result.code =="auth/invalid-phone-number") {
          this.errors.push("Invalid phone number. Number must be in the format: XXX-XXX-XXXX")
          this.mfaLoading = false;
        }
        else if(result && !result.code && !result.message) {
          this.mfaVerificationId = result;
          // do next step to enter phone code
          this.mfaStep = 'phoneCode';
        } else {
          this.errors.push('unable to send phone verification code.')
          console.error('sendPhoneCode error',result);
          // clear recaptcha
          this.recaptchaVerifier.clear();
        }
        this.mfaLoading = false;
      } else {
        this.mfaLoading = false;
        this.errors.push("invalid phone number for MFA.");
      }
    },
    handleRecaptchaResponse(response) {
      // reCAPTCHA solved, you can proceed with phoneAuthProvider.verifyPhoneNumber(...).
      // console.log('reCAPTCHA solved', response);
      console.log('reCAPTCHA solved');
      // do not move login stage here, required to have recaptcha element visible when calling sendPhoneVerification
      // this.sendPhoneCode();
      this.mfaStep = 'getPhoneNumber';
    },
    handleRecaptchaError(response) {
      // console.log('reCAPTCHA error', response);
      console.log('reCAPTCHA error');
      this.recaptchaVerifier.clear();
      this.recaptchaVerifier.reset();
    },
    resetRecaptcha() {
      // console.log('called resetRecaptcha');
      this.recaptchaVerifier.clear();
      this.recaptchaVerifier = null;
      this.createRecaptcha();
    },
    async createRecaptcha() {
      // v1 call from imported firebase library directly
      // wait for next tick so container is visible
      this.$nextTick(() => {
        this.recaptchaVerifier = new Firebase.auth.RecaptchaVerifier('recaptchaContainer', {
          // 'size': 'invisible',
          'size': 'normal',
          'callback': this.handleRecaptchaResponse,
          'expired-callback': this.resetRecaptcha,
          'error-callback': this.handleRecaptchaError,
        });
        this.recaptchaVerifier.verify();
      });
    },
    async enrollWithPhoneCode() {
      // console.log('enrollWithPhoneCode');
      // console.log('mfaVerificationId',this.mfaVerificationId);
      this.mfaLoading = true;
      let result = await this.$firebase.enrollPhoneMFA(this.user, this.mfaVerificationId, this.mfaCode);
      // console.log('enrollPhoneMFA result', result);
      if(!result) {
        // wait for redirect when user found
        console.log('successfully enrolled in MFA');
        this.mfaSuccess = true;
      } else {
        this.errors.push('unable to enroll with code.')
        console.log('enrollWithPhoneCode error', result);
      }
      this.mfaLoading = false;
    },
  },
  mounted () {
    // fill email box on page load
    if(this.user) {
      this.newEmail = this.user.email;
    }
  },

}
</script>

<style lang="less" scoped>

.input-display-name {
  margin: 0;
}
h3 {
  margin: 0 0 10px;
}
h3.tall {
  margin: 50px 0 10px;
}
a .fa-fw {
  margin-right: 5px;
}
hr {
  margin: 50px 0;
}
</style>
